import axios from 'axios';
import { readDeviceIdentity } from './deviceIdentity';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);
const HOSTING_HOSTS = new Set(['kassa.digitalergastro.app', 'digitalergastro-kassaapp.web.app', 'digitalergastro-kassaapp.firebaseapp.com']);

export function getBackendUrl() {
  if (typeof window !== 'undefined' && HOSTING_HOSTS.has(window.location.hostname)) return window.location.origin;
  const configuredUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();

  // Firebase Hosting forwards /api to Cloud Run. This also keeps platform-level
  // 502/503 responses readable by the browser, even before FastAPI is reached.
  if (configuredUrl === '/api' || configuredUrl === '/') return '';

  if (configuredUrl) {
    try {
      const url = new URL(configuredUrl);

      if (typeof window !== 'undefined' && LOCAL_HOSTS.has(window.location.hostname) && LOCAL_HOSTS.has(url.hostname)) {
        url.hostname = window.location.hostname;
      }

      return url.toString().replace(/\/$/, '').replace(/\/api$/, '');
    } catch {
      return configuredUrl.replace(/\/$/, '');
    }
  }

  if (typeof window !== 'undefined') {
    if (!LOCAL_HOSTS.has(window.location.hostname)) return window.location.origin;
    return `${window.location.protocol}//${window.location.hostname}:8000`;
  }

  return 'http://localhost:8000';
}

const API_BASE = `${getBackendUrl()}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const PRINT_JOBS_QUEUED_EVENT = 'fiscalpos:print-jobs-queued';

// True when a response queued a print job for this very device (e.g. the receipt
// printer on this Kasse), so the job runner can print immediately instead of
// waiting for its next poll.
export function responseQueuesJobForDevice(data, deviceId, depth = 0) {
  if (!deviceId || !data || typeof data !== 'object' || depth > 3) return false;
  if (Array.isArray(data)) return data.some((entry) => responseQueuesJobForDevice(entry, deviceId, depth + 1));
  if (data.job_id && data.device_id === deviceId) return true;
  return ['print_jobs', 'jobs'].some((key) => responseQueuesJobForDevice(data[key], deviceId, depth + 1));
}

// Add auth token and the id of this device (printer routing) to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('fiscalpos_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const device = readDeviceIdentity();
  if (device) {
    config.headers['X-Device-Id'] = device.id;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    const device = readDeviceIdentity();
    if (device && responseQueuesJobForDevice(response?.data, device.id) && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PRINT_JOBS_QUEUED_EVENT));
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const requestUrl = String(error?.config?.url || '');
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

    if (status === 401 && typeof window !== 'undefined' && !requestUrl.includes('/auth/login') && !requestUrl.includes('/auth/pin-login')) {
      localStorage.removeItem('fiscalpos_user');
      localStorage.removeItem('fiscalpos_token');

      if (currentPath.startsWith('/admin') && currentPath !== '/admin/login') {
        window.location.replace('/admin/login');
      } else if ((currentPath.startsWith('/pos') || currentPath.startsWith('/monitor')) && currentPath !== '/pos/login') {
        window.location.replace(`/pos/login?redirect=${encodeURIComponent(currentPath)}`);
      }
    }

    if (
      status === 403 &&
      typeof window !== 'undefined' &&
      currentPath.startsWith('/admin') &&
      requestUrl.startsWith('/admin')
    ) {
      localStorage.removeItem('fiscalpos_user');
      localStorage.removeItem('fiscalpos_token');
      window.location.replace('/admin/login');
    }

    return Promise.reject(error);
  },
);

// ============== AUTH ==============
export const login = (username, password) => 
  api.post('/auth/login', { username, password }).then(res => res.data);

export const pinLogin = (pin, companyId) =>
  api.post('/auth/pin-login', { pin, company_id: companyId || undefined }).then(res => res.data);

export const logout = (token) => 
  api.post('/auth/logout', { token }).then(res => res.data);

// ============== SUPERADMIN ==============
export const getCompanies = () => 
  api.get('/superadmin/companies').then(res => res.data);

export const createCompany = (data) => 
  api.post('/superadmin/companies', data).then(res => res.data);

export const createCompanyAdmin = (companyId, data) => 
  api.post(`/superadmin/companies/${companyId}/admin`, data).then(res => res.data);

// ============== USERS (Admin) ==============
export const getUsers = () =>
  api.get('/admin/users').then(res => res.data);

export const getReleases = (pageToken) =>
  api.get('/releases', { params: pageToken ? { page_token: pageToken } : {}, timeout: 30000 }).then(res => res.data);

export const createUser = (data) => 
  api.post('/admin/users', data).then(res => res.data);

export const updateUser = (id, data) => 
  api.put(`/admin/users/${id}`, data).then(res => res.data);

export const deleteUser = (id) => 
  api.delete(`/admin/users/${id}`).then(res => res.data);

// ============== CATEGORIES ==============
export const getCategories = () => 
  api.get('/categories').then(res => res.data);

export const createCategory = (data) => 
  api.post('/admin/categories', data).then(res => res.data);

export const updateCategory = (id, data) => 
  api.put(`/admin/categories/${id}`, data).then(res => res.data);

export const deleteCategory = (id) => 
  api.delete(`/admin/categories/${id}`).then(res => res.data);

// ============== PRODUCTS ==============
export const getProducts = (categoryId) => {
  const params = categoryId ? { category_id: categoryId } : {};
  return api.get('/products', { params }).then(res => res.data);
};

export const getProduct = (id) => 
  api.get(`/products/${id}`).then(res => res.data);

export const createProduct = (data) => 
  api.post('/admin/products', data).then(res => res.data);

export const updateProduct = (id, data) => 
  api.put(`/admin/products/${id}`, data).then(res => res.data);

export const deleteProduct = (id) => 
  api.delete(`/admin/products/${id}`).then(res => res.data);

// ============== ROOMS & TABLES ==============
export const getRooms = () => 
  api.get('/rooms').then(res => res.data);

export const createRoom = (name, sort_order = 0) => 
  api.post('/admin/rooms', null, { params: { name, sort_order } }).then(res => res.data);

export const updateRoom = (id, name, sort_order = 0) => 
  api.put(`/admin/rooms/${id}`, null, { params: { name, sort_order } }).then(res => res.data);

export const deleteRoom = (id) => 
  api.delete(`/admin/rooms/${id}`).then(res => res.data);

export const getTables = (roomId) => {
  const params = roomId ? { room_id: roomId } : {};
  return api.get('/tables', { params }).then(res => res.data);
};

export const createTable = (data) => 
  api.post('/admin/tables', data).then(res => res.data);

export const updateTable = (id, data) => 
  api.put(`/admin/tables/${id}`, data).then(res => res.data);

export const updateTableStatus = (id, status, orderId = null) => {
  const params = { status };
  if (orderId) params.order_id = orderId;
  return api.put(`/pos/tables/${id}/status`, null, { params }).then(res => res.data);
};

export const deleteTable = (id) =>
  api.delete(`/admin/tables/${id}`).then(res => res.data);

export const updateTablePosition = (id, x, y) =>
  api.put(`/admin/tables/${id}/position`, null, { params: { x, y } }).then(res => res.data);

// ============== ORDERS (POS) ==============
export const getOrders = (status, date, tableId) => {
  const params = {};
  if (status) params.status = status;
  if (date) params.date = String(date).replace(/-/g, '');
  if (tableId) params.table_id = tableId;
  return api.get('/pos/orders', { params }).then(res => res.data);
};

export const getOrder = (id) => 
  api.get(`/pos/orders/${id}`).then(res => res.data);

export const createOrder = (data) => 
  api.post('/pos/orders', data).then(res => res.data);

export const createOrderParty = (id, { label = '', guestCount = 0 } = {}) =>
  api.post(`/pos/orders/${id}/party`, {
    label,
    guest_count: guestCount,
  }).then(res => res.data);

export const mergeOrders = (id, targetOrderId) =>
  api.post(`/pos/orders/${id}/merge`, { target_order_id: targetOrderId }).then(res => res.data);

export const updateOrderItems = (id, items, discountAmount = 0, discountPercent = 0, discountReason = '') =>
  api.put(`/pos/orders/${id}/items`, {
    items, discount_amount: discountAmount, discount_percent: discountPercent, discount_reason: discountReason,
  }).then(res => res.data);

export const sendOrderCourse = (id, course) =>
  api.post(`/pos/orders/${id}/courses/send`, { course }).then(res => res.data);

export const transferOpenOrders = (targetCashierId) =>
  api.post('/pos/orders/transfer-open', { target_cashier_id: targetCashierId }).then(res => res.data);

export const splitOrderForPayment = (id, items) =>
  api.post(`/pos/orders/${id}/split-payment`, { items }).then(res => res.data);

export const addDiverseOrderItem = (orderId, { categoryId, description, price, vatRate, quantity = 1 }) =>
  api.post(`/pos/orders/${orderId}/items/diverse`, null, {
    params: { category_id: categoryId, description, price, vat_rate: vatRate, quantity },
  }).then(res => res.data);

export const moveOrderToTable = (id, tableId) =>
  api.put(`/pos/orders/${id}/table`, { table_id: tableId }).then(res => res.data);

export const completeOrder = (
  id,
  paymentMethod,
  paymentSplit = null,
  tipAmount = 0,
  internalBookingType = null,
  paymentDetails = null,
) => {
  const params = { payment_method: paymentMethod };
  const data = {
    payment_split: paymentSplit || [],
    tip_amount: tipAmount || 0,
    ...(internalBookingType ? { internal_booking_type: internalBookingType } : {}),
    ...(paymentDetails || {}),
  };
  return api.put(`/pos/orders/${id}/complete`, data, { params }).then(res => res.data);
};

export const authorizeGpTomPayment = (payload) =>
  api.post('/pos/payments/gptom/authorize', payload).then(res => res.data);

export const getGpTomTaskStatus = (taskId) =>
  api.get(`/pos/payments/gptom/tasks/${taskId}`).then(res => res.data);

export const listSumUpReaders = () =>
  api.get('/pos/payments/sumup/readers').then(res => res.data);

export const pairSumUpReader = (payload) =>
  api.post('/pos/payments/sumup/readers/pair', payload).then(res => res.data);

export const getSumUpReaderStatus = () =>
  api.get('/pos/payments/sumup/reader-status').then(res => res.data);

export const authorizeSumUpPayment = (payload) =>
  api.post('/pos/payments/sumup/authorize', payload).then(res => res.data);

export const getSumUpTransactionStatus = (clientTransactionId) =>
  api.get(`/pos/payments/sumup/transactions/${clientTransactionId}`).then(res => res.data);

export const cancelOrder = (id, reason = '', { reasonCode = '', wasProduced = true } = {}) =>
  api.put(`/pos/orders/${id}/cancel`, null, {
    params: { reason, reason_code: reasonCode, was_produced: wasProduced },
  }).then(res => res.data);

export const voidOrderItem = (orderId, cartKey, reason, { quantity = 0, reasonCode = '', wasProduced = true } = {}) =>
  api.put(`/pos/orders/${orderId}/items/void`, null, {
    params: { cart_key: cartKey, reason, quantity, reason_code: reasonCode, was_produced: wasProduced },
  }).then(res => res.data);

export const discountOrderItem = (orderId, cartKey, { quantity = 0, discountPercent = 0, discountAmount = 0, reason, account = '' }) =>
  api.put(`/pos/orders/${orderId}/items/discount`, null, {
    params: {
      cart_key: cartKey,
      quantity,
      discount_percent: discountPercent,
      discount_amount: discountAmount,
      reason,
      account,
    },
  }).then(res => res.data);

export const acceptOnlineOrder = (id, payload = {}) =>
  api.put(`/pos/orders/${id}/accept-online`, payload).then(res => res.data);

export const updateOnlineOrderStatus = (id, payload = {}) =>
  api.put(`/pos/orders/${id}/online-status`, payload).then(res => res.data);

export const parkOrder = (id) => 
  api.put(`/pos/orders/${id}/park`).then(res => res.data);

export const unparkOrder = (id) =>
  api.put(`/pos/orders/${id}/unpark`).then(res => res.data);

export const sendReceiptEmail = (id, email) =>
  api.post(`/pos/orders/${id}/send-receipt`, { email }).then(res => res.data);

// ============== CASH REGISTER ==============
export const getTodayCashRegister = () => 
  api.get('/pos/cash-register/today').then(res => res.data);

export const openCashRegister = (openingAmount, userId) => 
  api.post('/pos/cash-register/open', null, { params: { opening_amount: openingAmount, user_id: userId } }).then(res => res.data);

export const closeCashRegister = (closingAmount, userId, notes = '') => 
  api.post('/pos/cash-register/close', null, { params: { closing_amount: closingAmount, user_id: userId, notes } }).then(res => res.data);

// ============== REPORTS ==============
export const getDailySummary = (date, cashierId = null) => {
  const params = date ? { date: String(date).replace(/-/g, '') } : {};
  if (cashierId) params.cashier_id = cashierId;
  return api.get('/reports/daily-summary', { params }).then(res => res.data);
};

export const getTopProducts = (date, limit = 10, cashierId = null) => {
  const params = { limit };
  if (date) params.date = String(date).replace(/-/g, '');
  if (cashierId) params.cashier_id = cashierId;
  return api.get('/reports/top-products', { params }).then(res => res.data);
};

export const getHourlySales = (date) => {
  const params = date ? { date: String(date).replace(/-/g, '') } : {};
  return api.get('/reports/hourly-sales', { params }).then(res => res.data);
};

// ============== PRINTERS ==============
export const getPrinters = () => 
  api.get('/admin/printers').then(res => res.data);

export const discoverPrinters = () =>
  api.get('/admin/printers/discover').then(res => res.data);

export const createPrinter = (data) => 
  api.post('/admin/printers', data).then(res => res.data);

export const updatePrinter = (id, data) => 
  api.put(`/admin/printers/${id}`, data).then(res => res.data);

export const deletePrinter = (id) => 
  api.delete(`/admin/printers/${id}`).then(res => res.data);

export const getPrintGroups = () =>
  api.get('/admin/print-groups').then(res => res.data);

export const testPrinter = (id) =>
  api.post(`/admin/printers/${id}/test`).then(res => res.data);

export const createPrintGroup = (name, printerId = null) => 
  api.post('/admin/print-groups', null, { params: { name, printer_id: printerId } }).then(res => res.data);

// ============== DEVICES & PRINT JOBS ==============
// Printers always belong to a device. Device management uses the user session;
// job pickup uses the device token, so it keeps working while nobody is logged in.
export const getDevices = () =>
  api.get('/pos/devices').then(res => res.data);

export const registerDevice = (data) =>
  api.post('/pos/devices/register', data).then(res => res.data);

export const updateDevice = (deviceId, data) =>
  api.put(`/pos/devices/${deviceId}`, data).then(res => res.data);

export const deactivateDevice = (deviceId) =>
  api.delete(`/pos/devices/${deviceId}`).then(res => res.data);

export const getPrintJobs = (params = {}) =>
  api.get('/pos/print-jobs', { params }).then(res => res.data);

export const getPrintJob = (jobId) =>
  api.get(`/pos/print-jobs/${jobId}`).then(res => res.data);

export const retryPrintJob = (jobId) =>
  api.post(`/pos/print-jobs/${jobId}/retry`).then(res => res.data);

// Resolves with the finished job ({status: 'done' | 'failed', error}) or the last
// known state once the timeout is reached (e.g. the printer's device is offline).
export async function waitForPrintJob(jobId, { timeoutMs = 20000, intervalMs = 1000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  let job = null;
  while (Date.now() < deadline) {
    job = await getPrintJob(jobId);
    if (['done', 'failed', 'cancelled'].includes(job?.status)) return job;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return job;
}

// Separate client: a rejected device token must not log the waiter out.
const deviceApi = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });
deviceApi.interceptors.request.use((config) => {
  const device = readDeviceIdentity();
  if (device) {
    config.headers['X-Device-Token'] = device.token;
  }
  return config;
});

export const getMyDevice = () =>
  deviceApi.get('/devices/me').then(res => res.data);

export const claimDevicePrintJobs = () =>
  deviceApi.get('/devices/me/print-jobs').then(res => res.data);

export const reportDevicePrintJob = (jobId, status, error = '') =>
  deviceApi.post(`/devices/me/print-jobs/${jobId}/result`, { status, error }).then(res => res.data);

// ============== ARTICLE OPTIONS ==============
export const getArticleOptionGroups = () =>
  api.get('/article-option-groups').then(res => res.data);

export const createArticleOptionGroup = (data) =>
  api.post('/admin/article-option-groups', data).then(res => res.data);

export const updateArticleOptionGroup = (id, data) =>
  api.put(`/admin/article-option-groups/${id}`, data).then(res => res.data);

export const deleteArticleOptionGroup = (id) =>
  api.delete(`/admin/article-option-groups/${id}`).then(res => res.data);

// ============== FISCAL ==============
export const fiscalStartbeleg = () =>
  api.post('/admin/fiscal/startbeleg').then(res => res.data);

export const fiscalJahresbeleg = () =>
  api.post('/admin/fiscal/jahresbeleg').then(res => res.data);

export const fiscalNullbeleg = () =>
  api.post('/admin/fiscal/nullbeleg').then(res => res.data);

export const fiscalSchlussbeleg = () =>
  api.post('/admin/fiscal/schlussbeleg').then(res => res.data);

export const getFiscalReceipts = (params = {}) =>
  api.get('/admin/fiscal/receipts', { params }).then(res => res.data);

export const getFiscalStatus = () =>
  api.get('/admin/fiscal/status').then(res => res.data);

export const fiscalResyncOrders = () =>
  api.post('/admin/fiscal/resync-orders').then(res => res.data);

// ============== POS SETTINGS ==============
export const getPosSettingsPayload = () =>
  api.get('/pos/settings/payload').then(res => res.data);

export const savePosSettingsConfig = (posSettingsConfig) =>
  api.put('/pos/settings/config', { pos_settings_config: posSettingsConfig }).then(res => res.data);

export const updatePosPrintGroup = (groupId, payload) =>
  api.put(`/pos/settings/print-groups/${groupId}`, payload).then(res => res.data);

export const testPosPrinter = (printerId) =>
  api.post(`/pos/settings/printers/${printerId}/test`).then(res => res.data);

export const printOrderDocument = (orderId, target = 'receipt') =>
  api.post(`/pos/orders/${orderId}/print`, null, { params: { target } }).then(res => res.data);

export const printPosNullbeleg = () =>
  api.post('/pos/settings/fiscal/nullbeleg').then(res => res.data);

export const resyncPosFiscalOrders = () =>
  api.post('/pos/settings/fiscal/resync-orders').then(res => res.data);

export const uploadPosSupportLog = (payload) =>
  api.post('/pos/settings/support-log', payload).then(res => res.data);

// ============== LOGO ==============
export const uploadLogo = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/admin/settings/logo', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(res => res.data);
};

// ============== KASSENBUCH ==============
export const getKassenbuch = (params = {}) =>
  api.get('/admin/kassenbuch', { params }).then(res => res.data);

export const createKassenbuchEntry = (data) =>
  api.post('/admin/kassenbuch', data).then(res => res.data);

export const exportKassenbuch = (params = {}) =>
  api.get('/admin/kassenbuch/export', { params, responseType: 'blob' }).then(res => res.data);

// ============== ROLES ==============
export const getRoles = () =>
  api.get('/admin/roles').then(res => res.data);

export const createRole = (data) =>
  api.post('/admin/roles', data).then(res => res.data);

export const updateRole = (id, data) =>
  api.put(`/admin/roles/${id}`, data).then(res => res.data);

export const deleteRole = (id) =>
  api.delete(`/admin/roles/${id}`).then(res => res.data);

export const getUserEffectivePermissions = (userId) =>
  api.get(`/admin/roles/${userId}/effective-permissions`).then(res => res.data);

// ============== SETTINGS ==============
export const getSettings = () => {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname || '' : '';
  if (currentPath.startsWith('/pos') || currentPath.startsWith('/monitor')) {
    return getPosSettingsPayload().then((payload) => payload?.settings || null);
  }
  return api.get('/admin/settings').then(res => res.data);
};

export const updateSettings = (data) => 
  api.put('/admin/settings', data).then(res => res.data);

// ============== BILLING ==============
export const getBillingStatus = () =>
  api.get('/billing/status').then(res => res.data);

export const getBillingPlans = () =>
  api.get('/billing/plans').then(res => res.data);

export const createCheckoutSession = (planId) =>
  api.post('/billing/create-checkout', null, { params: { plan_id: planId } }).then(res => res.data);

// ============== PUBLIC WEBSHOP ==============
export const getPublicWebshop = (slug) =>
  api.get(`/public/webshop/${slug}`).then(res => res.data);

export const syncPublicWebshopCustomer = (slug, data) =>
  api.post(`/public/webshop/${slug}/customers/sync`, data).then(res => res.data);

export const createPublicWebshopOrder = (slug, data) =>
  api.post(`/public/webshop/${slug}/orders`, data).then(res => res.data);

// ============== POS TERMINAL ==============
export const getPosLayoutConfig = () =>
  api.get('/pos/layout-config').then(res => res.data);

// ============== OFFLINE SYNC ==============
export const getSyncData = () =>
  api.get('/pos/sync/data').then(res => res.data);

export const syncOfflineOrders = (orders) => 
  api.post('/pos/sync/orders', orders).then(res => res.data);

// ============== MONITOR ==============
export const getMonitorProfiles = () =>
  api.get('/monitor/profiles').then(res => res.data);

export const getMonitorProfile = (id) =>
  api.get(`/monitor/profiles/${id}`).then(res => res.data);

export const unlockMonitorSettings = (id, pin) =>
  api.post(`/monitor/profiles/${id}/unlock`, { pin }).then(res => res.data);

export const createMonitorProfile = (data) =>
  api.post('/monitor/profiles', data).then(res => res.data);

export const updateMonitorProfile = (id, data) =>
  api.put(`/monitor/profiles/${id}`, data).then(res => res.data);

export const getMonitorOrders = (companyId, printGroup) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (printGroup) params.print_group = printGroup;
  return api.get('/monitor/orders', { params }).then(res => res.data);
};

export const printMonitorTicket = (orderId, itemIds) =>
  api.post(`/monitor/orders/${orderId}/print`, { item_ids: itemIds }).then(res => res.data);

export const updateMonitorItemStep = (itemId, step) =>
  api.put(`/monitor/items/${itemId}/step`, null, { params: { step } }).then(res => res.data);

export const updateMonitorItemStatus = (itemId, status, readyQuantity = null) => {
  const params = { status };
  if (readyQuantity !== null && readyQuantity !== undefined) {
    params.ready_quantity = readyQuantity;
  }
  return api.put(`/monitor/items/${itemId}/status`, null, { params }).then(res => res.data);
};

export const markMonitorItemDone = (itemId) =>
  api.put(`/monitor/items/${itemId}/done`).then(res => res.data);

// ============== CUSTOMERS & LOYALTY (Kundenapp integration, Sprint 9) ==============
export const searchCustomers = (q) =>
  api.get('/pos/customers/search', { params: { q } }).then(res => res.data);

export const getCustomerBalance = (customerId) =>
  api.get(`/pos/customers/${customerId}/balance`).then(res => res.data);

export const getPosReservations = (dateOrParams, tableId) => {
  const params = (dateOrParams && typeof dateOrParams === 'object' && !Array.isArray(dateOrParams))
    ? { ...dateOrParams }
    : { date: dateOrParams, table_id: tableId };
  return api.get('/pos/reservations', { params }).then(res => res.data);
};

export const getPosReservationContext = () =>
  api.get('/pos/reservations/context').then(res => res.data);

export const updatePosReservationStatus = (reservationId, status) =>
  api.put(`/pos/reservations/${reservationId}/status`, { status }).then(res => res.data);

export const getPosActiveTimeLogs = () =>
  api.get('/pos/time-tracking/active').then(res => res.data);

export const provisionCompany = (companyId, data) =>
  api.post(`/admin/companies/${companyId}/provision`, data).then(res => res.data);

// ============== CUSTOMERS (Admin) ==============
export const getCustomers = (params = {}) =>
  api.get('/admin/customers', { params }).then(res => res.data);

export const createCustomer = (data) =>
  api.post('/admin/customers', data).then(res => res.data);

export const updateCustomer = (id, data) =>
  api.put(`/admin/customers/${id}`, data).then(res => res.data);

export const deleteCustomer = (id) =>
  api.delete(`/admin/customers/${id}`).then(res => res.data);

// ============== SEED & HEALTH ==============
export const seedData = () =>
  (localStorage.getItem('fiscalpos_token')
    ? api.post('/seed').then(res => res.data)
    : Promise.resolve({ skipped: true }));

export const healthCheck = () =>
  api.get('/health').then(res => res.data);

export default api;
